import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import { colors, typography } from '../../../styles/theme';
import { withAlpha } from '../../utils/sharedBranding';
import BrandedListingContent from './BrandedListingContent';
import LotShapeThumbnail from './LotShapeThumbnail';
import { RecordLotContentIcon } from '../RecordLotIdentity';
import { getCadStockColor } from '../../../Showroom/utils/cadStockStatus';
import { getStockStatusLabel } from '../../utils/listingCoreConstants';
import { formatShareCardMetaValue, resolveShareCardStockStatus } from '../../utils/listingPresentationUtils';

const CARD_BG = '#242424';
const CARD_BG_HOVER = '#2A2A2A';
const TEXT_MUTED = '#ABABAB';
const TEXT_STRONG = '#EDEDED';
const DIVIDER = 'rgba(255,255,255,0.08)';

/**
 * Alto fijo de la card de registro (`variant="record"`), igual para lote, UF y CAD
 * con o sin foto. Referencia: la card completa de UF —
 * header 43 (5 + 2 líneas de 16 + 5 + borde) y body 145 (8 + título 18 + 6 +
 * subtítulo 13 + 6 + caption/monto 39 + metas 6+17 + pie 6+16 + 10) = 188, más 4 de
 * aire. En grilla es el alto debajo de la media 16:9 (monto 22 → 186 + 4).
 * `footerExtra`: una fila más al pie (chips de lista en Registros): 6 + 22.
 * Los textos que no entran se cortan con ellipsis (título, subtítulo, caption y
 * metas en una línea; ubicación en dos).
 */
export const RECORD_CARD_HEIGHT = Object.freeze({ list: 192, grid: 190, footerExtra: 28 });

/**
 * Alto de la card compartida: el del registro, sin la fila de leads/consultas
 * (margen 6 + línea 16). El subtítulo queda reservado; si no hay, el aire va abajo.
 */
export const SHARE_CARD_HEIGHT = Object.freeze({
  list: RECORD_CARD_HEIGHT.list - 22,
  grid: RECORD_CARD_HEIGHT.grid - 22,
});

/** Alto de la card de registro en lista o del bloque de texto en grilla. */
export const getRecordCardHeight = (layout, { footerExtra = false } = {}) => (
  (layout === 'grid' ? RECORD_CARD_HEIGHT.grid : RECORD_CARD_HEIGHT.list)
  + (footerExtra ? RECORD_CARD_HEIGHT.footerExtra : 0)
);

const clampSx = (lines) => (lines === 1
  ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
  : {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  });

/**
 * La foto se posiciona en absoluto: así el alto lo fija el contenedor (3:2 en
 * grid, alto de la fila en lista) y nunca la imagen, que recorta con object-fit.
 */
/**
 * Plano local de la parcela CAD (`shape_local`: puntos 0..1, Y hacia abajo) en
 * `<svg>`, para showrooms sin georreferencia. Mismo look que la silueta.
 */
const ShapeLocalSvg = ({ shape, accentColor }) => {
  const ratio = shape.aspect_ratio || 1;
  // viewBox con el aspecto real y margen del 12 %: la forma entra entera.
  const w = ratio >= 1 ? 100 : 100 * ratio;
  const h = ratio >= 1 ? 100 / ratio : 100;
  const pad = 12;
  const pts = shape.points.map(([x, y]) => `${(x * w).toFixed(2)},${(y * h).toFixed(2)}`).join(' ');
  return (
    <Box
      data-testid="listing-cover-shape-local"
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg
        viewBox={`${-pad} ${-pad} ${w + 2 * pad} ${h + 2 * pad}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <polygon
          points={pts}
          fill={withAlpha(accentColor, 0.34)}
          stroke={accentColor}
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

/** Sin foto ni forma (CAD): ícono genérico de lote. */
const GenericShapeIcon = ({ accentColor }) => (
  <Box
    data-testid="listing-cover-icon"
    aria-hidden
    sx={{
      position: 'absolute',
      inset: 0,
      bgcolor: 'rgba(0,0,0,0.28)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: withAlpha(accentColor, 0.7),
    }}
  >
    <GridOnOutlinedIcon sx={{ fontSize: 28 }} />
  </Box>
);

const hasBoundary = (boundary) => Array.isArray(boundary) && boundary.length > 2;

/**
 * Prioridad: foto (portada o primera, ya resuelta en `url`) → silueta
 * georreferenciada (`boundary`) → plano local (`shapeLocal`, CAD) → ícono (CAD).
 * Si la foto no carga (onError) se sigue con el resto de la cadena.
 * Lote / UF sin `boundary` mantienen `LotShapeThumbnail` (grilla de plano).
 */
const CoverImage = ({ url, boundary, neighborBoundaries, accentColor, shapeLocal = null, isCad = false }) => {
  const [failedUrl, setFailedUrl] = useState(null);
  useEffect(() => { setFailedUrl(null); }, [url]);
  if (!url || failedUrl === url) {
    if (isCad && !hasBoundary(boundary)) {
      return shapeLocal
        ? <ShapeLocalSvg shape={shapeLocal} accentColor={accentColor} />
        : <GenericShapeIcon accentColor={accentColor} />;
    }
    return (
      <Box
        data-testid="listing-cover-shape"
        sx={{
          position: 'absolute',
          inset: 0,
        }}
      >
        <LotShapeThumbnail
          boundary={boundary}
          neighborBoundaries={neighborBoundaries}
          accentColor={accentColor}
        />
      </Box>
    );
  }

  return (
    <img
      src={url}
      alt="Portada del listado"
      data-testid="listing-cover-image"
      loading="lazy"
      decoding="async"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
      }}
      onError={() => setFailedUrl(url)}
    />
  );
};

/**
 * Badge de estado (Disponible / Reservado / Vendido…) fijo arriba a la izquierda
 * de la media, igual para lote, UF y CAD. Sin estado no se pinta (no deja hueco).
 * El texto puede bajar de línea: nunca se corta.
 */
const StockBadge = ({ status }) => (
  <Box
    data-testid="record-stock-badge"
    data-status={status}
    sx={{
      position: 'absolute',
      top: 6,
      left: 6,
      maxWidth: 'calc(100% - 12px)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      px: '6px',
      py: '2px',
      borderRadius: '999px',
      bgcolor: 'rgba(20,20,20,0.78)',
      border: '1px solid rgba(255,255,255,0.10)',
      zIndex: 1,
      pointerEvents: 'none',
    }}
  >
    <Box
      component="span"
      aria-hidden
      sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: getCadStockColor(status), flexShrink: 0 }}
    />
    <Typography
      component="span"
      sx={{
        fontFamily: typography.fontFamily.primary,
        fontSize: '10.5px',
        fontWeight: 600,
        lineHeight: '14px',
        color: TEXT_STRONG,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }}
    >
      {getStockStatusLabel(status)}
    </Typography>
  </Box>
);

/**
 * Tarjeta de item para listas compartidas (Terrio y branded) y para la vista
 * «por registro» de Registros / detalle de lista (`variant="record"`).
 * Jerarquía comercial: el monto es el protagonista y los datos registrales
 * quedan como referencia. Sin marca del dueño, el acento es el primary de Terrio.
 *
 * @param {'list'|'grid'} layout - 'list' apaisada (1 por fila), 'grid' vertical (2 por fila).
 * @param {'share'|'record'} variant - ambas usan el chrome compacto (badge de estado
 *   sobre la media, metas en una línea, alto fijo). 'record' suma el ⋮, la línea
 *   comercial y la fila de chips. El share no muestra leads ni consultas.
 */
export default function BrandedListingCard({
  item,
  accentColor,
  isActive = false,
  /** Resaltada desde el mapa (hover del chip de precio, F2). */
  isHighlighted = false,
  layout = 'list',
  onOpen,
  onLocate,
  onHover,
  boundary = null,
  neighborBoundaries = null,
  variant = 'share',
  menu = null,
  footer = null,
  testId = undefined,
  reserveFooterExtra = false,
}) {
  const [copied, setCopied] = useState(false);
  const accent = accentColor || colors.primary;
  const isGrid = layout === 'grid';
  const cadastralLabel = String(item.cadastralLabel ?? '');
  const copyIdLabel = item.copyIdLabel || 'Copiar número catastral';
  const locateLabel = isActive ? 'Quitar marca del mapa' : 'Ubicar en el mapa';
  const isRecord = variant === 'record';
  const stockStatus = isRecord ? (item.stock_status || null) : resolveShareCardStockStatus(item);
  const stockBadge = stockStatus ? <StockBadge status={stockStatus} /> : null;
  const metaLine = (item.metas || [])
    .map((meta) => {
      if (meta?.key === 'stock') return null;
      const value = isRecord ? (meta?.value != null ? String(meta.value) : '') : formatShareCardMetaValue(meta);
      const text = value != null ? String(value).trim() : '';
      return text ? { key: meta.key, label: meta.label, value: text } : null;
    })
    .filter(Boolean);
  const cardHeight = isRecord
    ? getRecordCardHeight(layout, { footerExtra: reserveFooterExtra })
    : (isGrid ? SHARE_CARD_HEIGHT.grid : SHARE_CARD_HEIGHT.list);
  const wrapSx = clampSx(2);

  const handleCopyCadastral = async (event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(cadastralLabel);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const actionButtons = (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', flexShrink: 0 }}>
      <Tooltip title={copied ? 'Copiado' : copyIdLabel} placement="top" arrow>
        <IconButton
          size="small"
          onClick={handleCopyCadastral}
          sx={{
            p: '4px',
            color: copied ? accent : TEXT_MUTED,
            '&:hover': { color: copied ? accent : '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
          }}
          aria-label={copyIdLabel}
        >
          {copied
            ? <CheckRoundedIcon sx={{ fontSize: 15 }} />
            : <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
      {onLocate ? (
        <Tooltip title={locateLabel} placement="top" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onLocate?.(item);
            }}
            sx={{
              p: '4px',
              color: isActive ? accent : TEXT_MUTED,
              '&:hover': { color: isActive ? accent : '#F9FAFB', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
            aria-label={locateLabel}
          >
            <MyLocationOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {menu}
    </Box>
  );

  const locationMeta = (
    <Typography
      title={`${item.locationLabel} · ${cadastralLabel}`}
      sx={{
        fontFamily: typography.fontFamily.primary,
        fontSize: '12px',
        lineHeight: '16px',
        color: TEXT_MUTED,
        minWidth: 0,
        flex: 1,
        ...wrapSx,
      }}
    >
      {item.locationLabel}
      {' · '}
      <Box
        component="span"
        sx={{
          fontSize: '11.5px',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color: TEXT_STRONG,
        }}
      >
        {cadastralLabel}
      </Box>
    </Typography>
  );

  const header = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
        px: '12px',
        py: '5px',
        borderBottom: `1px solid ${DIVIDER}`,
        minWidth: 0,
      }}
    >
      {locationMeta}
      {actionButtons}
    </Box>
  );

  const body = (
    <Box
      sx={{
        px: '12px',
        pt: '8px',
        pb: '10px',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <BrandedListingContent
        title={item.title}
        description={item.subtitle}
        wrapText
        singleLine
        hero={item.hero}
        compact
        titleAdornment={item.hasLotContent
          ? <RecordLotContentIcon summary={item.lotContentSummary || null} />
          : null}
        metas={[]}
        accentColor={accent}
        dense={isGrid}
      />
      {metaLine.length > 0 ? (
        <Typography
          data-testid="record-card-metas"
          sx={{
            mt: '6px',
            fontFamily: typography.fontFamily.primary,
            fontSize: isGrid ? '12px' : '12.5px',
            fontWeight: 500,
            lineHeight: '17px',
            color: TEXT_STRONG,
            ...clampSx(1),
          }}
        >
          {metaLine.map((meta, idx) => (
            <React.Fragment key={meta.key}>
              {idx > 0 ? <Box component="span" aria-hidden sx={{ color: TEXT_MUTED, mx: '5px' }}>·</Box> : null}
              <Box component="span" title={meta.label} sx={{ whiteSpace: 'nowrap' }}>{meta.value}</Box>
            </React.Fragment>
          ))}
        </Typography>
      ) : null}
      {footer ? (
        <Box sx={{ mt: '6px', minWidth: 0, overflow: 'hidden' }}>
          {footer}
        </Box>
      ) : null}
    </Box>
  );

  const cardSx = {
    borderRadius: '10px',
    bgcolor: isActive ? withAlpha(accent, 0.07) : CARD_BG,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    boxShadow: isActive
      ? `0 0 0 1px ${accent}`
      : (isHighlighted ? `0 0 0 2px ${accent}` : 'none'),
    ...(isHighlighted && !isActive ? { bgcolor: CARD_BG_HOVER } : null),
    '&:hover': { bgcolor: isActive ? withAlpha(accent, 0.1) : CARD_BG_HOVER },
  };

  const hoverHandlers = onHover
    ? {
      onMouseEnter: () => onHover(true),
      onMouseLeave: () => onHover(false),
    }
    : {};

  if (isGrid) {
    return (
      <Box
        onClick={() => onOpen?.(item)}
        {...hoverHandlers}
        data-testid={testId}
        data-located={isRecord && isActive ? 'true' : undefined}
        // Media 16:9 fija. El bloque de texto tiene alto fijo: el sobrante queda al pie.
        sx={{ ...cardSx, flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            aspectRatio: '16 / 9',
            minHeight: 0,
            flexShrink: 0,
            bgcolor: 'rgba(255,255,255,0.04)',
          }}
        >
          <CoverImage
            url={item.coverImage}
            boundary={boundary}
            neighborBoundaries={neighborBoundaries}
            accentColor={accent}
            shapeLocal={isRecord ? item.shapeLocal || null : null}
            isCad={isRecord && item.itemType === 'cad'}
          />
          {stockBadge}
        </Box>
        <Box
          data-testid="record-card-text"
          sx={{ height: cardHeight, flexShrink: 0, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}
        >
          {header}
          {body}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => onOpen?.(item)}
      {...hoverHandlers}
      data-testid={testId}
      data-located={isRecord && isActive ? 'true' : undefined}
      sx={{
        ...cardSx,
        display: 'flex',
        alignItems: 'stretch',
        height: cardHeight,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: { xs: 96, sm: 120 },
          minHeight: 0,
          height: '100%',
          flexShrink: 0,
          alignSelf: 'stretch',
          bgcolor: 'rgba(255,255,255,0.04)',
          borderRight: `1px solid ${DIVIDER}`,
        }}
      >
        <CoverImage
          url={item.coverImage}
          boundary={boundary}
          neighborBoundaries={neighborBoundaries}
          accentColor={accent}
          shapeLocal={isRecord ? item.shapeLocal || null : null}
          isCad={isRecord && item.itemType === 'cad'}
        />
        {stockBadge}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {header}
        {body}
      </Box>
    </Box>
  );
}
